/**
 * Sharing a meme on X. Each meme has a small page on the live site (public/m/NN.html) whose
 * card is the meme and which sends people on to the landing. Phones open the X app; desktop
 * opens the X web composer. Same approach as the dollarize share (Alberto, 3 October 2026):
 * the post text carries no domain, otherwise X builds the card from the home page.
 */

import { MEMES } from '../kit';

export const SITE = 'https://pepes.dog';

/**
 * The site links point at: the one the visitor is on (so sharing from preprod shares preprod),
 * or pepes.dog when that is a local address X could never reach.
 */
export function siteOrigin(origin: string): string {
  return /^https:\/\//.test(origin) && !/localhost|127\.0\.0\.1/.test(origin) ? origin : SITE;
}
const currentOrigin = () => siteOrigin(typeof window === 'undefined' ? SITE : window.location.origin);

export const memeSlug = (index: number) => String(index + 1).padStart(2, '0');
/** The meme's page: /meme/<descriptive name>, rendered by api/meme.js. */
export const memeSharePath = (index: number) => `/meme/${MEMES[index].name}`;
const memeUrl = (index: number, origin: string) => `${origin}${memeSharePath(index)}`;

/** Each meme is shared with its own joke (MEMES[i].share, Alberto 2026-10-12); the link carries the picture. */
export const shareText = (index: number) => MEMES[index].share;

export function shareIntentUrl(index: number, text: string, origin: string = currentOrigin()): string {
  const q = new URLSearchParams({ text, url: memeUrl(index, origin) });
  return `https://x.com/intent/post?${q}`;
}

/** The post opened in the X app, or null when the system has no app scheme to use. */
export function shareAppUrl(system: string, index: number, text: string, origin: string = currentOrigin()): string | null {
  const message = encodeURIComponent(`${text} ${memeUrl(index, origin)}`);
  if (system === 'iOS') return `twitter://post?message=${message}`;
  if (system === 'Android') {
    const web = encodeURIComponent(shareIntentUrl(index, text, origin));
    return `intent://post?message=${message}#Intent;scheme=twitter;package=com.twitter.android;S.browser_fallback_url=${web};end`;
  }
  return null;
}

export function systemOf(userAgent: string): 'iOS' | 'Android' | 'other' {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'iOS';
  if (/Android/i.test(userAgent)) return 'Android';
  return 'other';
}

/** Opens the share: the app on phones (the web composer if the iPhone has no app), a new tab elsewhere. */
export function shareMeme(index: number, _alt?: string): void {
  const text = shareText(index);
  const web = shareIntentUrl(index, text);
  const app = shareAppUrl(systemOf(navigator.userAgent), index, text);
  if (!app) {
    window.open(web, '_blank', 'noopener,noreferrer');
    return;
  }
  const started = Date.now();
  window.location.href = app;
  if (app.startsWith('twitter://')) {
    // no app: the page is still visible a moment later, so fall back to the web composer
    setTimeout(() => {
      if (document.visibilityState === 'visible' && Date.now() - started < 2500) window.location.href = web;
    }, 1500);
  }
}
